import React, { useEffect, useState } from 'react'
import { FormLayout } from '@/components/template/form-layout'
import { Layout } from '@/components/template/layout'
import { useTranslation } from 'react-i18next'
import { CustomCheckInForm } from '@/components/organisms/custom-check-in-form'
import { useAppSelector } from '@/app/hooks'
import { Button } from '@/components/atoms/button'
import { pcScreenStyle, spacerTopStyle } from '@/styles/common'
import { useForm } from 'react-hook-form'
import { formButtonStyle } from '@/styles/form'
import { AppDispatch } from '@/app/store'
import { useDispatch } from 'react-redux'
import { fetchCustomFields, setCustomCheckInData } from '@/features/checkIn/custom-check-in-field-slice'
import { getCheckInPayments } from '@/features/checkIn/check-in-payment-slice'
import { useQuerySearchParams } from '@/hooks/use-query-search-params'
import { errorHandler } from '@/libs/errors'
import { isEmpty } from 'lodash'
import { CheckInStep, CheckInStepName } from '@/components/organisms/checkIn/check-in-step'
import { AssignTimeSelectionInput, fetchTimeSelections, setSelectedTimes } from '@/features/checkIn/time-selection-slice'
import { TimeSelectionInForm } from '@/components/organisms/timeSelection/time-selection-in-form'
import { css } from '@emotion/react'
import { computedPaymentMethodByOperation, fetchPaymentSetting, PaymentSetting } from '@/features/hotel/payment-setting-by-hotel'
import { useNavigate } from 'react-router-dom'

export const CustomCheckIn: React.FC = () => {
  let ignoreUseEffectWithStrict = false
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const searchParams = useQuerySearchParams<{ hotelId: string }>()
  const navigate = useNavigate()
  const customCheckInFields = useAppSelector(state => state.customFields.customCheckin)
  const { timeSelections, selectedTimes, loading } = useAppSelector(state => state.timeSelection)
  const customCheckInFieldObj = customCheckInFields
    .filter(({ inputType }) => inputType !== 'none')
    .reduce((acc, currentField) => {
      acc[currentField.customCheckinId] = currentField.value

      return acc
    }, {})

  const paymentSetting = useAppSelector(state => state.paymentSetting.settings)
  // const { hasPaymentPlugins } = useAppSelector(state => state.hotelGuide)
  const checkInData = useAppSelector(state => state.checkIn.checkInData.fields)
  const [isFetchedComponentData, setIsFetchedComponentData] = useState(false)

  const useFormReturn = useForm({
    mode: 'all',
    shouldFocusError: true,
    defaultValues: { ...customCheckInFieldObj, timeSelections: selectedTimes },
  })
  const {
    handleSubmit,
    formState: { isValid },
  } = useFormReturn

  const makeNextUrl = async (opt: { assignSpaces?: AssignTimeSelectionInput[]; _paymentSetting?: PaymentSetting }) => {
    const latestPaymentSetting = opt._paymentSetting?.settings || paymentSetting
    if (!latestPaymentSetting.has_payment_plugin) {
      return '/checkin-confirm'
    }
    const paymentMethodValues = computedPaymentMethodByOperation(latestPaymentSetting?.payment_time || [], 'CHECKIN')

    let payments
    if (checkInData?.reservationIds) {
      payments = await dispatch(
        getCheckInPayments({
          reservationIds: checkInData.reservationIds,
          hotelId: searchParams.hotelId,
        }),
      ).unwrap()
    }
    const hasPaidTimeSelection = opt.assignSpaces?.some(space => space.price)
    if ((payments?.totalAmount > 0 || hasPaidTimeSelection) && paymentMethodValues.length) {
      return '/checkin-payment'
    } else {
      return '/checkin-confirm'
    }
  }

  const onSubmit = async formValues => {
    dispatch(setCustomCheckInData(customCheckInFields))
    const newState = await dispatch(
      setSelectedTimes({
        timeSelections,
        times: formValues.timeSelections,
        selectedReservations: checkInData.selectedReservations || [],
      }),
    ).unwrap()
    navigate(await makeNextUrl({ assignSpaces: newState.assignSpaces }))
  }

  const fetchComponentData = async () => {
    try {
      ignoreUseEffectWithStrict = true
      const selectReservationIds = checkInData?.selectedReservations?.map(reservation => reservation.reservationId) || []
      const [_customCheckInResp, _timeSelectionsResp, _paymentSetting] = await Promise.all([
        dispatch(fetchCustomFields({ hotelId: searchParams.hotelId })).unwrap(),
        selectReservationIds.length &&
          dispatch(
            fetchTimeSelections({
              hotelId: searchParams.hotelId,
              reservationsIds: selectReservationIds,
            }),
          ).unwrap(),
        dispatch(fetchPaymentSetting(searchParams.hotelId)).unwrap(),
      ])
      if (isEmpty(customCheckInFields) && isEmpty(_customCheckInResp?.customCheckin) && isEmpty(_timeSelectionsResp)) {
        const url = await makeNextUrl({ _paymentSetting })
        navigate(url, { replace: true })
        return
      }
      setIsFetchedComponentData(true)
    } catch (error) {
      console.error(error)
      errorHandler({ error })
    }
  }

  useEffect(() => {
    if (ignoreUseEffectWithStrict) {
      return
    }
    fetchComponentData()
  }, [])

  return (
    <Layout>
      {isFetchedComponentData && (
        <>
          <CheckInStep current={CheckInStepName.GuestInformation} />
          <form onSubmit={handleSubmit(onSubmit)} css={pcScreenStyle} style={{ paddingTop: '1rem' }}>
            {!isEmpty(customCheckInFields) && (
              <FormLayout>
                <CustomCheckInForm useFormReturn={useFormReturn} />
              </FormLayout>
            )}

            {!isEmpty(timeSelections) && !loading && (
              <FormLayout layoutCss={css({ marginTop: '1rem' })}>
                <TimeSelectionInForm useFormReturn={useFormReturn} />
              </FormLayout>
            )}

            <Button disabled={!isValid || loading} buttonCss={[formButtonStyle, spacerTopStyle]} text={t('Next')} type="submit" />
          </form>
        </>
      )}
    </Layout>
  )
}
